import { TRANSITION_DURATION } from '@fukku/slideshow/constants/constants'
import {
	type BulletIndexStore,
	createBulletIndexStore,
	getBulletIndexStoreInitialState,
} from '@fukku/slideshow/store/bulletIndexStore'
import {
	createIndexStore,
	type IndexStore,
} from '@fukku/slideshow/store/indexStore'
import {
	createPreloadedItemsStore,
	type PreloadedItemsStore,
} from '@fukku/slideshow/store/preloadedItemsStore'
import type {
	SlideshowFromDirection,
	UpdateSlideshowStylesProps,
} from '@fukku/slideshow/types/Slideshow'
import { useContextStore } from '@fukku/slideshow/utils/useContextStore'
import {
	type AnimationEventHandler,
	createContext,
	type JSX,
	type ReactNode,
	type RefObject,
	useCallback,
	useMemo,
	useRef,
} from 'react'
import type { StoreApi } from 'zustand/vanilla'

type SlideshowProviderProps = {
	readonly defaultActiveItemIndex: number
	readonly hasLoop: boolean
	readonly hasPreload: boolean
	readonly blockTouchEvents: boolean
	readonly onAnimationEnd: AnimationEventHandler<HTMLDivElement> | undefined
	readonly onSwipe:
		| ((fromDirection: SlideshowFromDirection) => void)
		| undefined
	readonly items: JSX.Element[]
	readonly children: ReactNode
}

export type SlideshowContextType = {
	defaultActiveItemIndex: number
	items: JSX.Element[]
	nItems: number
	hasLoop: boolean
	hasPreload: boolean
	readonly blockTouchEvents: boolean
	readonly onAnimationEnd: AnimationEventHandler<HTMLDivElement> | undefined
	readonly onSwipe:
		| ((fromDirection: SlideshowFromDirection) => void)
		| undefined
	slideshowRef: RefObject<HTMLDivElement | null>
	getSlideshowWidth: () => number
	activeItemIndexStore: StoreApi<IndexStore>
	bulletIndexStore: StoreApi<BulletIndexStore>
	preloadedItemsStore: StoreApi<PreloadedItemsStore>
	updateStyles: ({
		offset,
		transitionDuration,
	}: UpdateSlideshowStylesProps) => void
	setPosition: (index: number, withAnimation?: boolean) => void
}

export const SlideshowContext = createContext<SlideshowContextType | null>(null)

export const SlideshowProvider = ({
	defaultActiveItemIndex,
	items,
	hasLoop,
	hasPreload,
	blockTouchEvents,
	onAnimationEnd,
	onSwipe,
	children,
}: SlideshowProviderProps) => {
	const nItems = items.length

	/* Slideshow element in order to change its styles and know its width so we know how much offset is needed to apply */
	const slideshowRef = useRef<HTMLDivElement | null>(null)

	/* Item DOM index the user is seeing, so we know if a loop must be applied */
	const activeItemIndexStore = useContextStore(createIndexStore, {
		index: defaultActiveItemIndex,
	})
	activeItemIndexStore.subscribe((state) => {
		updateStyles({
			activeItemIndex: state.index,
			offset: 0,
			transitionDuration: TRANSITION_DURATION,
		})
	})

	const bulletIndexStore = useContextStore(
		createBulletIndexStore,
		getBulletIndexStoreInitialState({ defaultActiveItemIndex, nItems })
	)

	const preloadedItemsStore = useContextStore(createPreloadedItemsStore)

	const updateStyles = useCallback(
		({
			activeItemIndex,
			offset,
			transitionDuration,
		}: UpdateSlideshowStylesProps): void => {
			if (!slideshowRef.current) {
				return
			}

			if (activeItemIndex !== undefined) {
				slideshowRef.current.style.setProperty(
					'--slideshow-active-item-index',
					`${activeItemIndex}`
				)
			}

			if (offset !== undefined) {
				slideshowRef.current.style.setProperty(
					'--slideshow-offset',
					`${offset}px`
				)
			}
			if (transitionDuration !== undefined) {
				slideshowRef.current.style.setProperty(
					'--slideshow-transition-duration',
					`${transitionDuration}ms`
				)
			}
		},
		[]
	)

	const getSlideshowWidth = useCallback(
		(): number => slideshowRef.current?.getBoundingClientRect().width ?? 0,
		[]
	)

	const setPosition = useCallback(
		(index: number, withAnimation = false) => {
			activeItemIndexStore.setState({ index })
			bulletIndexStore.setState({ index })

			if (!withAnimation) {
				updateStyles({
					transitionDuration: 0,
				})
			}
		},
		[activeItemIndexStore, bulletIndexStore, updateStyles]
	)

	const value = useMemo(
		() => ({
			defaultActiveItemIndex,
			items,
			nItems,
			hasLoop,
			hasPreload,
			blockTouchEvents,
			onSwipe,
			onAnimationEnd,
			slideshowRef,
			activeItemIndexStore,
			bulletIndexStore,
			preloadedItemsStore,
			getSlideshowWidth,
			updateStyles,
			setPosition,
		}),
		[
			defaultActiveItemIndex,
			items,
			nItems,
			hasLoop,
			hasPreload,
			blockTouchEvents,
			onSwipe,
			onAnimationEnd,
			getSlideshowWidth,
			updateStyles,
			activeItemIndexStore,
			bulletIndexStore,
			preloadedItemsStore,
			setPosition,
		]
	)

	return (
		<SlideshowContext.Provider value={value}>
			{children}
		</SlideshowContext.Provider>
	)
}
